// Settings: the Billing tab (02-DESIGN/18-billing/02-the-billing-page.md;
// 02-DESIGN/03-console/01-the-console.md § Settings: the Billing tab;
// decision 0073 § 11), drawn where the platform profile says the install
// bills and only for an admin. On a root, top to bottom: the line, the plans
// as cards in the catalogue's order, the add-ons as rows, the bar while a
// selection differs with *Update plan*, which opens the confirmation in the
// tab's place, *Billing details*, *Payment method*, *Receipts* and, apart,
// *Cancel*. It speaks the billing service's verbs and nothing else, and shows
// the prices the catalogue sets. A child's tab says its root's plan covers it
// and nothing more. Forms stand in the tab's place (decision 0069); the
// browser goes to the payment provider's page and comes back to this tab.

import { useEffect, useState } from "react";

import { followable } from "../../address/address.ts";
import {
  Button,
  Confirm,
  describe,
  Field,
  NotAllowed,
  Notice,
  Pending,
  Pill,
  TextInput,
  useLoad,
  type Load,
} from "../../ui/components.tsx";
import { FormPage, useFormState } from "../../ui/form.tsx";
import { Table, type Column } from "../../ui/table.tsx";
import { type TenantReply } from "../../wire/auth.ts";
import {
  ChargeStatus,
  Code,
  LONG,
  SERVICE,
  State,
  Subjects,
  type AddOn,
  type ApplyReply,
  type CancelReply,
  type CatalogueReply,
  type Charge,
  type ChargesListReply,
  type Details,
  type DetailsPutRequest,
  type DetailsReply,
  type MethodChangeReply,
  type PreviewReply,
  type RetryReply,
  type SubscriptionReply,
} from "../../wire/billing.ts";
import { useSurface } from "../surface.tsx";
import {
  amountWords,
  appliedLine,
  barLine,
  billingRefusal,
  cancelLine,
  changeOf,
  childLine,
  cleaned,
  complete,
  differs,
  failedCharge,
  grants,
  isSwitch,
  lineWords,
  nextLine,
  offered,
  onlyWith,
  planName,
  priceWords,
  RECEIPTS_LINE,
  returnedLine,
  selectionOf,
  statusTone,
  statusWords,
  tabLine,
  unitWords,
  VAT_NEEDED,
  vatNeeded,
  viesLine,
  type Selection,
} from "./billing.ts";
import "./billing.css";
import { dayOf, holderOf } from "./plan.ts";
import { receiptPath } from "./receipts.ts";

/** The tab's forms, named in the history entry: the confirmation and the details. */
type BillingForm = "billing/confirm" | "billing/details";

/** CHECKOUT_KEY is where the tab notes, for this browser tab only, that it sent the person to the payment page. */
const CHECKOUT_KEY = "impire.billing.checkout";

/** noteCheckout keeps which plan the person went to pay for, so the tab says how it went on the way back. */
function noteCheckout(slug: string, plan: string) {
  try {
    sessionStorage.setItem(CHECKOUT_KEY, JSON.stringify({ slug, plan }));
  } catch {
    // Without storage the tab only reads the subscription again.
  }
}

/** checkoutOf is the plan the person went to pay for in this workspace, if the tab sent them. */
function checkoutOf(slug: string): string | undefined {
  try {
    const raw = sessionStorage.getItem(CHECKOUT_KEY);
    if (raw === null) {
      return undefined;
    }
    const v = JSON.parse(raw) as { slug?: unknown; plan?: unknown };
    return v.slug === slug && typeof v.plan === "string" ? v.plan : undefined;
  } catch {
    return undefined;
  }
}

function forgetCheckout() {
  try {
    sessionStorage.removeItem(CHECKOUT_KEY);
  } catch {
    // nothing kept
  }
}

/** goPay sends the browser to the payment provider's page, noting the plan; an address that is not https is refused. */
function goPay(url: string, slug: string, plan: string): boolean {
  if (!followable(url)) {
    return false;
  }
  noteCheckout(slug, plan);
  window.location.assign(url);
  return true;
}

/** Refused shows a refusal in the page's words where it is one of billing's, and as it is otherwise. */
function Refused({ err, what, root }: { err: unknown; what: string; root?: string }) {
  const said = billingRefusal(err, root);
  if (said === undefined) {
    return <NotAllowed err={err} what={what} />;
  }
  return (
    <Notice kind="bad" title={`${what}: refused`}>
      {said}
    </Notice>
  );
}

export function BillingTab({ held }: { held: Load<TenantReply> }) {
  const s = useSurface();
  if (held.state === "pending") {
    return <Pending what="Reading the plan" />;
  }
  if (held.state === "refused") {
    return <NotAllowed err={held.err} what="Billing" />;
  }
  const h = holderOf(held.value.tenant, s.name);
  if (h.child) {
    return (
      <div className="card">
        <p>{childLine(h.name)}</p>
      </div>
    );
  }
  return <RootBilling />;
}

/** Said is a notice the tab shows after something it did. */
interface Said {
  ok: boolean;
  text: string;
}

function RootBilling() {
  const s = useSurface();
  const form = useFormState<BillingForm>();
  const [tick, setTick] = useState(0);
  const again = () => {
    setTick((t) => t + 1);
  };
  const [catalogue] = useLoad(
    "billing/catalogue",
    () => s.bus.call<CatalogueReply>(SERVICE, Subjects.catalogueGet(), undefined),
    tick,
    { keep: true },
  );
  const [reply] = useLoad(
    "billing/subscription",
    () => s.bus.call<SubscriptionReply>(SERVICE, Subjects.subscriptionGet(), undefined),
    tick,
    { keep: true },
  );
  const [first] = useLoad(
    "billing/charges",
    () => s.bus.call<ChargesListReply>(SERVICE, Subjects.chargesList(), {}),
    tick,
    { keep: true },
  );
  const [picked, setPicked] = useState<Selection | undefined>(undefined);
  const [said, setSaid] = useState<Said | undefined>(undefined);
  const [returned] = useState(() => checkoutOf(s.tenant));
  useEffect(() => {
    if (returned !== undefined) {
      forgetCheckout();
    }
  }, [returned]);

  const cat = catalogue.state === "ready" ? catalogue.value : undefined;
  const r = reply.state === "ready" ? reply.value : undefined;
  const charges = first.state === "ready" ? first.value.charges : [];
  const sub = r?.subscription;
  const sel = picked ?? selectionOf(sub);
  const pending = cat !== undefined && r !== undefined && differs(sel, sub, cat);
  const change = cat !== undefined ? changeOf(sel, cat) : undefined;
  const [previewTick, setPreviewTick] = useState(0);
  const [preview] = useLoad(
    `billing/preview/${pending && change !== undefined ? JSON.stringify(change) : "none"}`,
    () =>
      pending && change !== undefined
        ? s.bus.call<PreviewReply>(SERVICE, Subjects.changePreview(), change, LONG)
        : Promise.resolve<PreviewReply | undefined>(undefined),
    previewTick,
  );

  if (reply.state === "pending" || catalogue.state === "pending") {
    return <Pending what="Reading billing" />;
  }
  if (reply.state === "refused") {
    return <Refused err={reply.err} what="Billing" />;
  }
  if (catalogue.state === "refused") {
    return <Refused err={catalogue.err} what="Plans" />;
  }
  if (r === undefined || cat === undefined) {
    return null;
  }

  const reset = () => {
    setPicked(undefined);
  };
  const done = (text: string, ok = true) => {
    setSaid({ ok, text });
    reset();
    again();
  };

  if (form.open === "billing/confirm") {
    if (!pending || preview.state === "refused") {
      // Nothing to confirm any more — a refresh, or a selection taken back.
      return <ConfirmGone err={preview.state === "refused" ? preview.err : undefined} onClose={form.close} />;
    }
    return (
      <ConfirmChange
        preview={preview.state === "ready" ? preview.value : undefined}
        details={sub?.details}
        planName={planName(cat, sel.plan)}
        onClose={() => {
          form.close();
        }}
        onKeep={() => {
          reset();
          form.close();
        }}
        onAgain={() => {
          setPreviewTick((t) => t + 1);
          again();
        }}
        onApplied={(text) => {
          form.close();
          done(text);
        }}
      />
    );
  }
  if (form.open === "billing/details") {
    return (
      <FormPage
        heading="h2"
        title="Billing details"
        line="Who Impire invoices, and where the invoices and a failed payment's notice go."
        back="Billing"
        onClose={form.close}
      >
        <DetailsForm
          initial={sub?.details}
          submit="Save"
          onCancel={form.close}
          onSaved={() => {
            form.close();
            setSaid({ ok: true, text: "The billing details are saved." });
            again();
          }}
        />
      </FormPage>
    );
  }

  const failed = failedCharge(r, charges);
  const line = tabLine(s.name, r, cat, failed);
  const next = nextLine(s.name, sub, cat);
  const back =
    returned !== undefined && first.state === "ready"
      ? returnedLine(s.name, planName(cat, returned), charges[0])
      : undefined;
  return (
    <div className={pending ? "billing with-bar" : "billing"}>
      <div className="card">
        <h2>
          {line.text}
          {line.undo && (
            <>
              {" "}
              <UndoCancel
                onDone={() => {
                  done(`${s.name} keeps its plan.`);
                }}
              />
            </>
          )}
        </h2>
        {next !== undefined && <p className="muted">{next}</p>}
        {back !== undefined && said === undefined && (
          <Notice kind={back.ok ? "ok" : "bad"}>{back.text}</Notice>
        )}
        {said !== undefined && <Notice kind={said.ok ? "ok" : "bad"}>{said.text}</Notice>}
      </div>

      <section className="section">
        <div className="section-head">
          <h2>Plans</h2>
        </div>
        <div className="plan-cards">
          {cat.plans.map((p) => {
            const current = sub !== undefined && sub.state !== State.ended && sub.plan === p.name;
            const chosen = sel.plan === p.name;
            return (
              <div key={p.name} className={chosen ? "card plan-card chosen" : "card plan-card"}>
                <h3>{p.display_name}</h3>
                <p className="plan-price">{priceWords(p.price_cents, cat.rate_bp)}</p>
                <ul className="plan-grants">
                  {grants(p).map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
                {current && !chosen ? (
                  <Button
                    size="sm"
                    onClick={() => {
                      setPicked({ ...sel, plan: p.name });
                    }}
                  >
                    Current plan
                  </Button>
                ) : current ? (
                  <Pill tone="ok">Current plan</Pill>
                ) : (
                  <Button
                    size="sm"
                    kind={chosen ? "primary" : "default"}
                    onClick={() => {
                      setPicked({ ...sel, plan: p.name });
                    }}
                  >
                    {chosen ? "Selected" : "Select"}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {cat.add_ons.length > 0 && (
        <section className="section">
          <div className="section-head">
            <h2>Add-ons</h2>
          </div>
          <div className="card">
            <dl className="kv">
              {cat.add_ons.map((a) => (
                <AddOnRow
                  key={a.name}
                  a={a}
                  plan={sel.plan}
                  only={onlyWith(a, cat)}
                  quantity={sel.add_ons[a.name] ?? 0}
                  bp={cat.rate_bp}
                  onChange={(q) => {
                    setPicked({ ...sel, add_ons: { ...sel.add_ons, [a.name]: q } });
                  }}
                />
              ))}
            </dl>
          </div>
        </section>
      )}

      <section className="section">
        <div className="section-head">
          <h2>Billing details</h2>
          <Button
            size="sm"
            onClick={() => {
              form.show("billing/details");
            }}
          >
            {sub?.details !== undefined ? "Change" : "Add"}
          </Button>
        </div>
        <div className="card">
          <DetailsSummary d={sub?.details} />
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Payment method</h2>
        </div>
        <div className="card">
          <PaymentMethod reply={r} onDone={done} />
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>Receipts</h2>
        </div>
        <p className="small muted">{RECEIPTS_LINE}</p>
        <Receipts first={first} />
      </section>

      {sub !== undefined && (sub.state === State.active || sub.state === State.pastDue) && (
        <section className="section billing-cancel">
          <div className="section-head">
            <h2>Cancel</h2>
          </div>
          <div className="card stack">
            <p>{cancelLine(s.name, sub)}</p>
            <CancelPlan
              onDone={() => {
                done(`${s.name}'s plan ends on ${dayOf(sub.cycle.end)}.`);
              }}
            />
          </div>
        </section>
      )}

      {pending && (
        <div className="billing-bar" role="region" aria-label="Update plan">
          <span className="grow">
            {preview.state === "pending" && "Pricing…"}
            {preview.state === "ready" && preview.value !== undefined && barLine(preview.value)}
          </span>
          <Button kind="quiet" onClick={reset}>
            Keep the current plan
          </Button>
          <Button
            kind="primary"
            disabled={preview.state !== "ready"}
            onClick={() => {
              setSaid(undefined);
              form.show("billing/confirm");
            }}
          >
            Update plan
          </Button>
        </div>
      )}
    </div>
  );
}

/** AddOnRow is one add-on: its name, its price a unit, and a quantity or a switch; dimmed where the selected plan does not offer it. */
function AddOnRow({
  a,
  plan,
  only,
  quantity,
  bp,
  onChange,
}: {
  a: AddOn;
  plan: string;
  only: string | undefined;
  quantity: number;
  bp: number | undefined;
  onChange: (q: number) => void;
}) {
  const on = plan !== "" && offered(a, plan);
  const price = priceWords(a.unit_price_cents, bp).replace(" a month", " each a month");
  return (
    <>
      <dt className={on ? undefined : "dimmed"}>
        {a.display_name}
        <div className="small faint">
          {unitWords(a)}
          {only !== undefined && ` · ${only}`}
        </div>
      </dt>
      <dd className={on ? "add-on-row" : "add-on-row dimmed"}>
        <span className="small muted">{price}</span>
        {isSwitch(a) ? (
          <label className="checkbox">
            <input
              type="checkbox"
              checked={quantity > 0}
              disabled={!on}
              onChange={(e) => {
                onChange(e.target.checked ? 1 : 0);
              }}
            />
            {quantity > 0 ? "On" : "Off"}
          </label>
        ) : (
          <input
            className="input add-on-quantity"
            type="number"
            min={0}
            max={99}
            value={quantity}
            disabled={!on}
            aria-label={`${a.display_name}: how many`}
            onChange={(e) => {
              const n = Number(e.target.value);
              onChange(Number.isFinite(n) && n > 0 ? Math.min(Math.floor(n), 99) : 0);
            }}
          />
        )}
      </dd>
    </>
  );
}

/** ConfirmGone stands where the confirmation was when there is nothing to confirm any more. */
function ConfirmGone({ err, onClose }: { err: unknown; onClose: () => void }) {
  return (
    <FormPage
      heading="h2"
      title="Update plan"
      back="Billing"
      onClose={onClose}
      foot={<Button onClick={onClose}>Back</Button>}
    >
      {err !== undefined ? (
        <Refused err={err} what="Update plan" />
      ) : (
        <p className="muted">Nothing is selected to change. Choose a plan on the Billing tab.</p>
      )}
    </FormPage>
  );
}

/**
 * ConfirmChange is the confirmation in the tab's place: what changes, line
 * by line, the two amounts and the date, *Update plan* — or *Continue to
 * payment* where a payment method is still to be set up — and *Keep the
 * current plan*. The details are asked for here the first time.
 */
function ConfirmChange({
  preview,
  details,
  planName: name,
  onClose,
  onKeep,
  onAgain,
  onApplied,
}: {
  preview: PreviewReply | undefined;
  details: Details | undefined;
  planName: string;
  onClose: () => void;
  onKeep: () => void;
  onAgain: () => void;
  onApplied: (text: string) => void;
}) {
  const s = useSurface();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [stale, setStale] = useState(false);
  const [askDetails, setAskDetails] = useState(false);
  if (preview === undefined) {
    return (
      <FormPage heading="h2" title="Update plan" back="Billing" onClose={onClose}>
        <Pending what="Pricing" />
      </FormPage>
    );
  }
  const needDetails = preview.details_required || askDetails;
  const apply = () => {
    if (busy) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    setStale(false);
    s.bus.call<ApplyReply>(SERVICE, Subjects.changeApply(), { hash: preview.hash }, LONG).then(
      (reply) => {
        setBusy(false);
        if (reply.checkout_url !== undefined && reply.checkout_url !== "") {
          if (!goPay(reply.checkout_url, s.tenant, previewPlan(preview, name))) {
            setErr(new Error("The payment page's address is not one this console follows."));
          }
          return;
        }
        onApplied(appliedLine(s.name, preview, name));
      },
      (e: unknown) => {
        setBusy(false);
        const code = typeof e === "object" && e !== null && "code" in e ? e.code : undefined;
        if (code === Code.previewStale) {
          setStale(true);
          onAgain();
          return;
        }
        if (code === Code.detailsRequired) {
          setAskDetails(true);
          return;
        }
        setErr(e);
      },
    );
  };
  const amounts = barLine(preview).split(" · ");
  return (
    <FormPage
      heading="h2"
      title="Update plan"
      line={`What changes for ${s.name}.`}
      back="Billing"
      onClose={onClose}
      foot={
        needDetails ? undefined : (
          <>
            <Button kind="quiet" disabled={busy} onClick={onKeep}>
              Keep the current plan
            </Button>
            <Button kind="primary" disabled={busy} onClick={apply}>
              {busy ? "Updating…" : preview.first_payment ? "Continue to payment" : "Update plan"}
            </Button>
          </>
        )
      }
    >
      {stale && (
        <Notice kind="info">
          Prices or dates changed while you were deciding; here is the current amount.
        </Notice>
      )}
      {err !== undefined && <Refused err={err} what="Update plan" />}
      <div className="card">
        <ul className="plan-lines">
          {preview.lines.map((l) => (
            <li key={`${l.kind}/${l.name}`}>{lineWords(l)}</li>
          ))}
        </ul>
        {amounts.map((a) => (
          <p key={a} className="plan-amount">
            {a}
          </p>
        ))}
        {preview.first_payment && !needDetails && (
          <p className="small muted">You will be taken to our payment page to set one up.</p>
        )}
      </div>
      {needDetails && (
        <section className="section">
          <div className="section-head">
            <h3>Billing details</h3>
          </div>
          <p className="small muted">Who Impire invoices, asked once before the first plan.</p>
          <DetailsForm
            initial={details}
            submit="Save and continue"
            onCancel={onKeep}
            onSaved={() => {
              setAskDetails(false);
              onAgain();
            }}
          />
        </section>
      )}
    </FormPage>
  );
}

/** previewPlan is the plan a preview prices, by its display name, for the way back from the payment page. */
function previewPlan(p: PreviewReply, fallback: string): string {
  return p.lines.find((l) => l.kind === "plan")?.name ?? fallback;
}

/** DetailsSummary is the details as kept, with the VAT register's answer in words. */
function DetailsSummary({ d }: { d: Details | undefined }) {
  if (d === undefined) {
    return <p className="muted">None yet. They are asked for when a plan is first chosen.</p>;
  }
  const vies = viesLine(d);
  return (
    <dl className="kv">
      <dt>Legal name</dt>
      <dd>{d.legal_name}</dd>
      <dt>Email address</dt>
      <dd>{d.email}</dd>
      <dt>Address</dt>
      <dd>
        {[d.address.line1, d.address.line2, `${d.address.postal_code} ${d.address.city}`, d.country]
          .filter((x) => x !== undefined && x.trim() !== "")
          .join(", ")}
      </dd>
      {d.vat_number !== undefined && d.vat_number !== "" && (
        <>
          <dt>VAT number</dt>
          <dd>
            <span className="mono">{d.vat_number}</span>
            {vies !== undefined && <div className="small muted">{vies}</div>}
          </dd>
        </>
      )}
    </dl>
  );
}

/** draftOf is the details form before anything is typed: what is kept, or nothing. */
function draftOf(d: Details | undefined): DetailsPutRequest {
  return {
    legal_name: d?.legal_name ?? "",
    email: d?.email ?? "",
    address: {
      line1: d?.address.line1 ?? "",
      line2: d?.address.line2 ?? "",
      postal_code: d?.address.postal_code ?? "",
      city: d?.address.city ?? "",
    },
    country: d?.country ?? "",
    vat_number: d?.vat_number ?? "",
  };
}

/**
 * DetailsForm is the billing details' fields, saved on billing.details.put:
 * the VAT number checked at the register before the reply, its answer and
 * every refusal in the design's words.
 */
function DetailsForm({
  initial,
  submit,
  onCancel,
  onSaved,
}: {
  initial: Details | undefined;
  submit: string;
  onCancel: () => void;
  onSaved: (d: DetailsReply) => void;
}) {
  const s = useSurface();
  const [d, setD] = useState<DetailsPutRequest>(() => draftOf(initial));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const body = cleaned(d);
  const ok = complete(body);
  const set = (patch: Partial<DetailsPutRequest>) => {
    setD({ ...d, ...patch });
  };
  const setAddress = (patch: Partial<DetailsPutRequest["address"]>) => {
    setD({ ...d, address: { ...d.address, ...patch } });
  };
  const save = () => {
    if (!ok || busy) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    s.bus.call<DetailsReply>(SERVICE, Subjects.detailsPut(), body, LONG).then(
      (reply) => {
        setBusy(false);
        onSaved(reply);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      {err !== undefined && <Refused err={err} what="Save" />}
      <Field label="Legal name" hint="The name the invoices are made out to.">
        <TextInput
          value={d.legal_name}
          onChange={(v) => {
            set({ legal_name: v });
          }}
          disabled={busy}
          autoFocus
        />
      </Field>
      <Field label="Email address" hint="Where the invoices and a failed payment's notice go.">
        <TextInput
          value={d.email}
          onChange={(v) => {
            set({ email: v });
          }}
          disabled={busy}
        />
      </Field>
      <Field label="Address">
        <TextInput
          value={d.address.line1}
          onChange={(v) => {
            setAddress({ line1: v });
          }}
          disabled={busy}
        />
      </Field>
      <Field label="Address, second line" hint="Optional.">
        <TextInput
          value={d.address.line2 ?? ""}
          onChange={(v) => {
            setAddress({ line2: v });
          }}
          disabled={busy}
        />
      </Field>
      <div className="two">
        <Field label="Postal code">
          <TextInput
            value={d.address.postal_code}
            onChange={(v) => {
              setAddress({ postal_code: v });
            }}
            disabled={busy}
          />
        </Field>
        <Field label="City">
          <TextInput
            value={d.address.city}
            onChange={(v) => {
              setAddress({ city: v });
            }}
            disabled={busy}
          />
        </Field>
      </div>
      <Field label="Country" hint="Two letters: BE, NL, DE.">
        <TextInput
          value={d.country}
          onChange={(v) => {
            set({ country: v });
          }}
          disabled={busy}
        />
      </Field>
      <Field
        label="VAT number"
        hint={vatNeeded(d.country) ? VAT_NEEDED : "With the country's letters in front: BE0123456789."}
      >
        <TextInput
          value={d.vat_number ?? ""}
          onChange={(v) => {
            set({ vat_number: v });
          }}
          mono
          disabled={busy}
        />
      </Field>
      <div className="rows">
        <Button kind="quiet" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" kind="primary" disabled={!ok || busy}>
          {busy ? "Checking…" : submit}
        </Button>
      </div>
    </form>
  );
}

/** PaymentMethod is the method as the page says it, *Change*, and past due *Try again* beside it. */
function PaymentMethod({
  reply,
  onDone,
}: {
  reply: SubscriptionReply;
  onDone: (text: string, ok?: boolean) => void;
}) {
  const s = useSurface();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const sub = reply.subscription;
  const detail = sub?.mollie?.detail;
  const live = sub !== undefined && sub.state !== State.ended;
  const change = () => {
    setBusy(true);
    setErr(undefined);
    s.bus.call<MethodChangeReply>(SERVICE, Subjects.methodChange(), undefined, LONG).then(
      (r) => {
        setBusy(false);
        if (!goPay(r.checkout_url, s.tenant, sub?.plan ?? "")) {
          setErr(new Error("The payment page's address is not one this console follows."));
        }
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  const retry = () => {
    setBusy(true);
    setErr(undefined);
    s.bus.call<RetryReply>(SERVICE, Subjects.retry(), undefined, LONG).then(
      (r) => {
        setBusy(false);
        onDone(
          r.charge.status === ChargeStatus.failed
            ? "The payment failed again; update your payment method."
            : "The payment is on its way.",
          r.charge.status !== ChargeStatus.failed,
        );
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <div className="stack">
      {err !== undefined && <Refused err={err} what="Payment method" />}
      <div className="rows">
        <span className="grow">
          {detail !== undefined && detail !== ""
            ? detail
            : "None yet. It is set up when a plan is first bought."}
        </span>
        {live && (
          <Button size="sm" disabled={busy} onClick={change}>
            Change
          </Button>
        )}
        {sub?.state === State.pastDue && (
          <Button size="sm" kind="primary" disabled={busy} onClick={retry}>
            Try again
          </Button>
        )}
      </div>
    </div>
  );
}

/** COLUMNS are the receipts' columns: the date, what it was, the amount with its VAT, the status, the invoice number. */
const COLUMNS: readonly Column<Charge>[] = [
  {
    key: "date",
    header: "Date",
    width: "8rem",
    value: (c) => c.created,
    text: (c) => dayOf(c.created),
    cell: (c) => dayOf(c.created),
  },
  { key: "what", header: "What", value: (c) => c.description, cell: (c) => c.description },
  {
    key: "amount",
    header: "Amount",
    value: (c) => c.amount.gross_cents,
    text: (c) => amountWords(c.amount),
    cell: (c) => amountWords(c.amount),
  },
  {
    key: "status",
    header: "Status",
    width: "8rem",
    facet: "Status",
    value: (c) => statusWords(c.status),
    cell: (c) => <Pill tone={statusTone(c.status)}>{statusWords(c.status)}</Pill>,
  },
  {
    key: "invoice",
    header: "Invoice",
    width: "8rem",
    narrow: false,
    value: (c) => c.invoice?.number ?? "",
    cell: (c) => c.invoice?.number ?? "",
  },
];

/** Receipts is the root's charges, newest first, each a page of its own; *More* reads the page before. */
function Receipts({ first }: { first: Load<ChargesListReply> }) {
  const s = useSurface();
  const [more, setMore] = useState<Charge[]>([]);
  const [next, setNext] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  if (first.state === "pending") {
    return <Pending what="Reading the receipts" />;
  }
  if (first.state === "refused") {
    return <Refused err={first.err} what="Receipts" />;
  }
  const rows = [...first.value.charges, ...more];
  const cursor = more.length > 0 ? next : first.value.next;
  if (rows.length === 0) {
    return <p className="muted">No receipts yet.</p>;
  }
  const readMore = () => {
    if (cursor === undefined || busy) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    s.bus.call<ChargesListReply>(SERVICE, Subjects.chargesList(), { before: cursor }).then(
      (r) => {
        setBusy(false);
        setMore([...more, ...r.charges]);
        setNext(r.next);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  return (
    <>
      <Table
        label="Receipts"
        rows={rows}
        columns={COLUMNS}
        rowKey={(c) => c.id}
        sort={{ key: "date", dir: "desc" }}
        to={(c) => receiptPath(s.tenant, c.id)}
      />
      {err !== undefined && <Refused err={err} what="Receipts" />}
      {cursor !== undefined && (
        <div className="rows">
          <Button size="sm" kind="quiet" disabled={busy} onClick={readMore}>
            {busy ? "Reading…" : `More receipts (${String(first.value.total - rows.length)} older)`}
          </Button>
        </div>
      )}
    </>
  );
}

/** CancelPlan is *Cancel*, confirmed once. */
function CancelPlan({ onDone }: { onDone: () => void }) {
  const s = useSurface();
  const [err, setErr] = useState<unknown>(undefined);
  const [busy, setBusy] = useState(false);
  return (
    <div className="stack">
      {err !== undefined && <Refused err={err} what="Cancel" />}
      <div className="rows">
        <Confirm
          label="Cancel the plan"
          consequence="Cancel at the end of this month's cycle?"
          disabled={busy}
          onConfirm={() => {
            setBusy(true);
            setErr(undefined);
            s.bus.call<CancelReply>(SERVICE, Subjects.cancel(), undefined).then(
              () => {
                setBusy(false);
                onDone();
              },
              (e: unknown) => {
                setBusy(false);
                setErr(e);
              },
            );
          }}
        />
      </div>
    </div>
  );
}

/** UndoCancel is *Undo* on the tab's line until the cycle ends. */
function UndoCancel({ onDone }: { onDone: () => void }) {
  const s = useSurface();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  return (
    <>
      <Button
        size="sm"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          setErr(undefined);
          s.bus.call<CancelReply>(SERVICE, Subjects.cancelUndo(), undefined).then(
            () => {
              setBusy(false);
              onDone();
            },
            (e: unknown) => {
              setBusy(false);
              setErr(e);
            },
          );
        }}
      >
        Undo
      </Button>
      {err !== undefined && <span className="small">{billingRefusal(err) ?? describe(err)}</span>}
    </>
  );
}
